import { useCallback, useEffect, useReducer } from "react";

import { useHistoryState } from "@/utils/hooks/wizard/useHistory.hook";
import { scrollToTop } from "@/utils/scroll";

import {
    goToStep as goToStepAction,
    nextStep as nextStepAction,
    reset as resetAction,
    setState as setStateAction
} from "./wizard.actions";
import { WizardReducer } from "./wizard.reducer";
import { StepMeta, WizardStateOf } from "./wizard.types";

type UseWizardReturn<T, TStepMeta> = {
    state: T;
    currentStep?: StepMeta<TStepMeta>;
    steps: Array<StepMeta<TStepMeta>>;
    nextStep: () => void;
    previousStep: () => void;
    goToStep: (step: number) => void;
    setState: (newState: Partial<T>) => void;
    canGoBack: boolean;
    canGoForward: boolean;
    reset: () => void;
};

export const useWizard = <T, TStepMeta>(
    steps: Array<StepMeta<TStepMeta>>,
    initialState: T,
    options: {
        scrollToTop: boolean;
    } = { scrollToTop: false }
): UseWizardReturn<T, TStepMeta> => {
    const { getHistoryState, pushHistoryState, replaceHistoryState } =
        useHistoryState<T>();

    const [state, dispatch] = useReducer(WizardReducer, {
        state: initialState,
        currentStep: 0,
        steps: steps || [],
        history: [initialState]
    } as WizardStateOf<T, TStepMeta>);

    useEffect(() => {
        const historyState = getHistoryState();

        if (historyState?.currentStep === state.currentStep) return;

        if (historyState) {
            pushHistoryState({
                state: state.state,
                currentStep: state.currentStep
            });
        } else {
            replaceHistoryState({
                state: state.state,
                currentStep: state.currentStep
            });
        }
    }, [
        state.currentStep,
        state.state,
        getHistoryState,
        pushHistoryState,
        replaceHistoryState
    ]);

    useEffect(() => {
        const abortController = new AbortController();

        window.addEventListener(
            "popstate",
            () => {
                const historyState = getHistoryState();

                if (!historyState) return;
                if (historyState.currentStep === state.currentStep) return;

                if (historyState.currentStep > state.currentStep) {
                    dispatch(setStateAction(historyState.state));
                }

                dispatch(goToStepAction(historyState.currentStep));
            },
            { signal: abortController.signal }
        );

        return () => {
            abortController.abort();
        };
    }, [getHistoryState, state.currentStep]);

    const scrollCheck = useCallback(() => {
        if (options.scrollToTop) {
            scrollToTop();
        }
    }, [options.scrollToTop]);

    const nextStep = useCallback(() => {
        scrollCheck();
        dispatch(nextStepAction());
    }, [scrollCheck]);

    const previousStep = useCallback(() => {
        scrollCheck();
        history.back();
    }, [scrollCheck]);

    const goToStep = useCallback(
        (step: number) => {
            scrollCheck();

            const stepsToGoBack = state.currentStep - step;

            history.go(-stepsToGoBack);
        },
        [scrollCheck, state.currentStep]
    );

    const setState = useCallback(
        (newState: Partial<T>) => {
            replaceHistoryState({
                state: { ...state.state, ...newState },
                currentStep: state.currentStep
            });
            dispatch(setStateAction(newState));
        },
        [replaceHistoryState, state.currentStep, state.state]
    );

    const canGoBack = state.currentStep > 0;
    const canGoForward = state.currentStep < state.steps.length - 1;

    const reset = useCallback(() => {
        scrollCheck();
        dispatch(resetAction({ initialState, steps }));
    }, [initialState, steps, scrollCheck]);

    return {
        state: state.state,
        currentStep: state.steps.find(
            (step) => step.index === state.currentStep
        ),
        steps: state.steps,
        nextStep,
        previousStep,
        goToStep,
        canGoBack,
        canGoForward,
        setState,
        reset
    };
};
